import { useState, type FormEvent, type KeyboardEvent } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { ApiError } from "@/api/client";
import { useAskNewConversation, useIsAdmin, useProviders, useSourceLanding } from "@/api/queries";
import { Button } from "@/components/ui/Button";
import { ArrowRightIcon, SparkIcon } from "@/components/icons";
import { usePreferredSource } from "@/lib/preferredSource";
import { cn } from "@/lib/cn";

/**
 * The front page's first job: get a question asked.
 *
 * A source, a box and the openers that source's schema suggests — the same
 * ones an empty conversation offers (`GET /connections/{id}/landing`), so a
 * first question can be one click. Asking creates the conversation, sends the
 * question and opens it; the chat page reattaches to the run that is already
 * streaming. The source is the shared working context (`usePreferredSource`),
 * so it is whichever one the reader last worked in.
 */
export function AskPanel() {
  const { sources, connectionId, select } = usePreferredSource();
  const { data: providers } = useProviders();
  const isAdmin = useIsAdmin();
  const landing = useSourceLanding(connectionId);
  const ask = useAskNewConversation();
  const navigate = useNavigate();
  const [text, setText] = useState("");

  const noProvider = providers !== undefined && !providers.some((provider) => provider.enabled);
  const blocked = !connectionId || noProvider;
  const failure = ask.error instanceof ApiError ? ask.error.message : ask.error ? "The question could not be sent." : null;

  function submit(question: string) {
    const trimmed = question.trim();
    if (!trimmed || blocked || ask.isPending) return;
    select(connectionId);
    ask.mutate(
      { connection_id: connectionId, text: trimmed },
      {
        onSuccess: ({ thread }) =>
          void navigate({ to: "/chat/$threadId", params: { threadId: thread.id } }),
      },
    );
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    submit(text);
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    // Return sends, Shift+Return is a new line — the composer's convention.
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submit(text);
    }
  }

  if (!sources.length) {
    return (
      <section className="bg-card border-border rounded-container shadow-card border p-5">
        <p className="type-h4 text-ink">No data sources yet</p>
        <p className="type-support text-ink-secondary mt-1">
          {isAdmin
            ? "Connect a database or upload a file, and questions about it can be asked here."
            : "An administrator has not connected a database yet. Once one is, you can ask about it here."}
        </p>
        {isAdmin ? (
          <Link
            to="/sources"
            className="type-support text-accent mt-3 inline-flex items-center gap-1 hover:underline"
          >
            Add a source
            <ArrowRightIcon className="size-3.5" />
          </Link>
        ) : null}
      </section>
    );
  }

  return (
    <section
      aria-label="Ask a question"
      className="bg-card border-border rounded-container shadow-card border p-4 sm:p-5"
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="home-source" className="type-label text-ink">
            Ask about
          </label>
          <select
            id="home-source"
            value={connectionId}
            onChange={(event) => select(event.target.value)}
            className="control-sm bg-surface text-ink type-label border-border hover:border-border-emphasized focus:border-accent rounded-element border px-2"
          >
            {sources.map((source) => (
              <option key={source.id} value={source.id}>
                {source.name}
              </option>
            ))}
          </select>
          {landing.data && !landing.data.documented ? (
            <span className="type-caption text-warning normal-case">undocumented source</span>
          ) : null}
        </div>
        <div className="border-border focus-within:border-accent rounded-chat bg-surface flex items-end gap-2 border p-2 ps-4 motion-fast transition-colors">
          <textarea
            aria-label="Your question"
            rows={2}
            value={text}
            disabled={ask.isPending}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder={
              noProvider
                ? "Add a model provider before asking a question."
                : "Ask a question in plain English…"
            }
            className="type-body text-ink placeholder:text-ink-disabled max-h-48 min-h-12 flex-1 resize-none bg-transparent py-1.5 outline-none"
          />
          <Button
            type="submit"
            variant="primary"
            loading={ask.isPending}
            disabled={blocked || !text.trim()}
          >
            Ask
          </Button>
        </div>
        {failure ? <p className="type-support text-error">{failure}</p> : null}
        {noProvider ? (
          <p className="type-support text-ink-secondary">
            {isAdmin ? (
              <>
                No model provider is enabled.{" "}
                <Link to="/settings/providers" className="text-accent hover:underline">
                  Add one
                </Link>{" "}
                to start answering.
              </>
            ) : (
              "No model provider is enabled yet — an administrator needs to add one."
            )}
          </p>
        ) : null}
      </form>

      {landing.data?.suggestions.length ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="type-caption text-ink-disabled inline-flex items-center gap-1">
            <SparkIcon className="size-3.5" />
            Try
          </span>
          {landing.data.suggestions.map((suggestion) => (
            <button
              key={suggestion.label}
              type="button"
              disabled={blocked || ask.isPending}
              title={suggestion.question}
              onClick={() => submit(suggestion.question)}
              className={cn(
                "border-border text-ink-secondary type-support rounded-full border px-3 py-1",
                "hover:border-accent hover:text-accent motion-fast transition-colors",
                "disabled:cursor-not-allowed disabled:opacity-50",
              )}
            >
              {suggestion.label}
            </button>
          ))}
        </div>
      ) : null}
    </section>
  );
}
